// src/components/profiles/ProfilePage.tsx

"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter, usePathname } from "next/navigation";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getMemberProfile,
  getGuestProfile,
} from "../../redux/profiles/profileSlice";
import { getProfileType } from "@/redux/profiles/profileBootstrapSlice";

import {
  selectAuthUser,
  selectProfileLoadingStatus,
  selectProfileError,
  selectActiveProfileType,
  selectMemberProfile,
  selectGuestProfile,
} from "../../redux/selectors/profile/userSelectors";

import { useAlertMessage } from "../../hooks/common/useAlertMessage";
import { AlertModal, PageSkeleton } from "../../components/common";
import getBackendMessage from "../../utils/errors/getBackendMessage";

import MemberProfile from "./Member/MemberProfile";
import GuestUserProfile from "./GuestUser/GuestUserProfile";
import "./ProfilePage.css";

type ResolvedProfileType = "member" | "guest" | "child" | "unknown" | null;

const getTitleForStatus = (status?: number) => {
  if (status === 401) return "Unauthorized";
  if (status === 403) return "Access denied";
  if (status === 404) return "Profile not found";
  if (typeof status === "number" && status >= 500) return "Server error";
  return "Profile loading failed";
};

const isLoadingStatus = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "loading";
  }

  return false;
};

const isTemporaryProfileGateError = (error: unknown): boolean => {
  const anyError = error as any;

  const data =
    anyError?.response?.data ??
    anyError?.data ??
    anyError;

  const code = String(data?.code ?? "")
    .trim()
    .toLowerCase();

  const detail = String(data?.detail ?? data?.error ?? "")
    .trim()
    .toLowerCase();

  return (
    code === "profile_temporarily_unavailable" ||
    code === "profile_temporarily_limited" ||
    detail.includes("profile is currently unavailable") ||
    detail.includes("profile is temporarily unavailable")
  );
};

const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();

  const authUser = useAppSelector(selectAuthUser);
  const activeProfileType = useAppSelector(selectActiveProfileType);

  const memberProfile = useAppSelector(selectMemberProfile);
  const guestProfile = useAppSelector(selectGuestProfile);

  const profileLoadingStatus = useAppSelector(selectProfileLoadingStatus);
  const profileError = useAppSelector(selectProfileError);

  const [bootLoading, setBootLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [bootProfileType, setBootProfileType] =
    useState<ResolvedProfileType>(null);

  const [localMemberProfile, setLocalMemberProfile] =
    useState<any>(null);

  const [localGuestProfile, setLocalGuestProfile] =
    useState<any>(null);

  const { message, showMessage, clearMessage } = useAlertMessage();

  const profileLoading = isLoadingStatus(profileLoadingStatus);

  useEffect(() => {
    if (authUser === null) {
      return;
    }

    if (authUser?.is_deleted) {
      router.replace("/reactivate-account");
      return;
    }

    if (!authUser && pathname !== "/") {
      router.replace("/");
    }
  }, [
    authUser,
    pathname,
    router,
  ]);

  useEffect(() => {
    if (
      !authUser ||
      authUser.is_deleted
    ) {
      setBootLoading(false);
      return;
    }

    let cancelled = false;

    const boot = async () => {
      setBootLoading(true);

      try {
        const result = await dispatch(getProfileType()).unwrap();

        if (cancelled) return;

        const profileType = (result?.profile_type ?? null) as ResolvedProfileType;
        setBootProfileType(profileType);

        if (profileType === "member") {
          const profile = await dispatch(getMemberProfile()).unwrap();

          if (cancelled) return;

          setLocalMemberProfile(profile);
          setLocalGuestProfile(null);
        } else if (profileType === "guest") {
          const profile = await dispatch(getGuestProfile()).unwrap();

          if (cancelled) return;

          setLocalGuestProfile(profile);
          setLocalMemberProfile(null);
        }
      } catch (error) {
        if (cancelled) return;

        // If older backend still returns a temporary gate as 403,
        // do not keep the page booting forever.
        if (isTemporaryProfileGateError(error)) {
          setBootProfileType("member");
        }
      } finally {
        if (!cancelled) {
          setBootLoading(false);
        }
      }
    };

    boot();

    return () => {
      cancelled = true;
    };
  }, [dispatch, authUser?.id]);

  const backend = useMemo(
    () => (profileError ? getBackendMessage(profileError) : null),
    [profileError]
  );

  useEffect(() => {
    if (!backend) return;

    if (backend.status === 401) {
      router.replace("/");
      return;
    }

    if (isTemporaryProfileGateError(profileError)) {
      setShowErrorModal(false);
      clearMessage();
      return;
    }

    if (backend.status && backend.status !== 404) {
      showMessage(backend.text, "error");
      setShowErrorModal(true);
    }
  }, [backend, profileError, router, showMessage, clearMessage]);

  const resolvedProfileType =
    (activeProfileType as ResolvedProfileType) || bootProfileType;

  const resolvedMemberProfile =
    memberProfile || localMemberProfile;

  const resolvedGuestProfile =
    guestProfile || localGuestProfile;

  const resolvedProfile =
    resolvedProfileType === "member"
      ? resolvedMemberProfile
      : resolvedProfileType === "guest"
        ? resolvedGuestProfile
        : null;

  const showSkeleton =
    bootLoading ||
    retrying ||
    profileLoading ||
    !resolvedProfileType ||
    !resolvedProfile;

  if (authUser === null || showSkeleton) {
    return (
      <PageSkeleton
        variant="profile"
        showTextLines={4}
        className="profile-loading-skeleton"
      />
    );
  }

  if (!authUser) {
    return (
      <PageSkeleton
        variant="profile"
        showTextLines={4}
        className="profile-loading-skeleton"
      />
    );
  }

  return (
    <>
      {showErrorModal && backend && backend.status !== 401 && (
        <AlertModal
          type="error"
          title={getTitleForStatus(backend.status)}
          showTimer
          autoCloseDuration={8000}
          closable
          onClose={() => {
            setShowErrorModal(false);
            clearMessage();
          }}
          primaryActionLabel="Retry"
          onPrimaryAction={() => {
            setShowErrorModal(false);
            clearMessage();
            setRetrying(true);

            const thunk =
              resolvedProfileType === "member"
                ? getMemberProfile()
                : getGuestProfile();

            dispatch(thunk)
              .unwrap()
              .then((profile) => {
                if (resolvedProfileType === "member") {
                  setLocalMemberProfile(profile);
                } else {
                  setLocalGuestProfile(profile);
                }
              })
              .finally(() => setRetrying(false));
          }}
          secondaryActionLabel="Back to Home"
          onSecondaryAction={() => {
            setShowErrorModal(false);
            clearMessage();
            router.replace("/");
          }}
        >
          {message?.text ?? backend.text}
        </AlertModal>
      )}

      {resolvedProfileType === "member" && resolvedMemberProfile ? (
        <MemberProfile member={resolvedMemberProfile} />
      ) : resolvedProfileType === "guest" && resolvedGuestProfile ? (
        <GuestUserProfile guest={resolvedGuestProfile} />
      ) : (
        <PageSkeleton
          variant="profile"
          showTextLines={4}
          className="profile-loading-skeleton"
        />
      )}
    </>
  );
};

export default ProfilePage;